/**
 * Centralized price parsing and formatting utilities
 * Handles European format (1.234,56) and US format (1,234.56)
 */

/**
 * Parse price string to number
 * Handles various formats:
 * - "1.234,56 €" -> 1234.56
 * - "€ 1,234.56" -> 1234.56
 * - "1234.56" -> 1234.56
 * - "1.234" -> 1234 (ambiguous, assumes European thousands)
 */
export function parsePrice(price: string | number | null | undefined): number {
  if (typeof price === 'number') return price;
  if (!price) return 0;
  
  // Remove currency symbols and spaces
  let clean = price.toString().replace(/[€$\s]/g, '').trim();
  if (!clean) return 0;
  
  // Detect format by checking if comma appears after dot
  // European: 1.234,56 (dot for thousands, comma for decimal)
  // US: 1,234.56 (comma for thousands, dot for decimal)
  
  const hasDot = clean.includes('.');
  const hasComma = clean.includes(',');
  
  if (hasDot && hasComma) {
    // Both present - check which comes last
    const dotPos = clean.lastIndexOf('.');
    const commaPos = clean.lastIndexOf(',');
    
    if (commaPos > dotPos) {
      // European format: 1.234,56
      clean = clean.replace(/\./g, '').replace(',', '.');
    } else {
      // US format: 1,234.56
      clean = clean.replace(/,/g, '');
    }
  } else if (hasComma && !hasDot) {
    // Only comma - check position
    const commaPos = clean.lastIndexOf(',');
    const decimals = clean.substring(commaPos + 1);
    
    if (decimals.length === 2) {
      // Likely European decimal: 1234,56
      clean = clean.replace(',', '.');
    } else {
      // Likely US thousands: 1,234
      clean = clean.replace(/,/g, '');
    }
  }
  // If only dot, assume US format (already correct for parseFloat)
  
  const parsed = parseFloat(clean);
  return isNaN(parsed) ? 0 : parsed;
}

/**
 * Format number as European price string
 * 1234.56 -> "€ 1.234,56"
 */
export function formatPrice(price: number | string | null | undefined, currency: string = 'EUR'): string {
  const numericPrice = typeof price === 'string' ? parsePrice(price) : (price || 0);
  
  return new Intl.NumberFormat('de-AT', {
    style: 'currency',
    currency: currency
  }).format(numericPrice);
}
